/**
 * Date utility functions for consistent timezone handling.
 *
 * Problem: Date-only strings (e.g., "2024-02-15") are parsed as UTC midnight by `new Date()`.
 * For users in negative UTC offset timezones, this causes dates to display one day earlier.
 * Similarly, `toISOString()` converts to UTC, which can shift dates for users in positive offsets.
 *
 * Solution: Always work with local time when dealing with date-only values.
 */

/**
 * Parses a date-only string (YYYY-MM-DD) as local midnight instead of UTC midnight.
 * This ensures the date displays correctly regardless of the user's timezone.
 *
 * A full ISO timestamp falls through to standard parsing, which keeps its
 * time-of-day and therefore lands on the viewer's calendar day.
 *
 * ### The anchor is load-bearing (#1229)
 *
 * The pattern used to be unanchored at the end, so `2026-08-04T23:45:00Z`
 * matched the date-only branch — the fallback the comment promised was
 * unreachable for exactly the input the comment named. The consequence was not
 * cosmetic: the function took the **UTC** calendar date and built a **local**
 * midnight from it, discarding time-of-day, so a timestamp near a day boundary
 * rendered as the UTC day rather than the viewer's.
 *
 * | Stored (UTC)           | Viewer     | Correct local | Rendered before |
 * |------------------------|------------|---------------|-----------------|
 * | `2026-08-04T23:45:00Z` | UTC+2 CEST | 2026-08-05    | **2026-08-04**  |
 * | `2026-08-05T00:15:00Z` | UTC−5 EST  | 2026-08-04    | **2026-08-05**  |
 *
 * Off by one day in opposite directions, over a window the size of the UTC
 * offset. This is the *inverse* of the bug the file was written to fix: the
 * date-only branch is correct for genuine date-only strings, and was quietly
 * wrong for timestamps it was never meant to receive but did — `granted_at` on
 * the sharing surfaces being the live case.
 *
 * @param dateString - A date string in YYYY-MM-DD format
 * @returns A Date object set to local midnight, or null if invalid
 */
export function parseDateAsLocal(dateString: string): Date | null {
  if (!dateString) return null;

  // Anchored at BOTH ends: a date-only string, and nothing else. See above.
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    // Fall back to standard parsing for other formats (e.g., full ISO timestamps)
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  const [, year, month, day] = match;
  // Create date using local time (months are 0-indexed)
  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
}

/**
 * Formats a Date object to a YYYY-MM-DD string using local date components.
 * This avoids the UTC conversion that toISOString() performs.
 *
 * @param date - A Date object
 * @returns A date string in YYYY-MM-DD format
 */
export function formatDateToLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a date string for display in ISO format (YYYY-MM-DD).
 * Handles date-only strings correctly by parsing as local time first.
 *
 * @param dateString - A date string (YYYY-MM-DD or full ISO timestamp)
 * @returns A date string in YYYY-MM-DD format for display
 */
export function formatDateForDisplay(dateString: string): string {
  const date = parseDateAsLocal(dateString);
  if (!date) return dateString;
  return formatDateToLocalString(date);
}

/**
 * Formats a date to ISO format for display: YYYY-MM-DD
 * Accepts both Date objects and date strings.
 *
 * @param dateInput - A Date object or date string
 * @returns A date string in YYYY-MM-DD format, or empty string if invalid
 */
export function formatDateISO(dateInput: Date | string): string {
  if (!dateInput) return '';

  const date = typeof dateInput === 'string' ? parseDateAsLocal(dateInput) : dateInput;
  if (!date || isNaN(date.getTime())) return '';

  return formatDateToLocalString(date);
}

/**
 * Formats a datetime to ISO format with time: YYYY-MM-DD HH:MM
 * Accepts both Date objects and date strings.
 *
 * @param dateInput - A Date object or datetime string
 * @returns A datetime string in YYYY-MM-DD HH:MM format, or empty string if invalid
 */
export function formatDateTimeISO(dateInput: Date | string): string {
  if (!dateInput) return '';

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return '';

  const datePart = formatDateToLocalString(date);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${datePart} ${hours}:${minutes}`;
}

/**
 * Checks if a date-only string represents a date in the past.
 * Compares using local dates to avoid timezone issues.
 *
 * @param dateString - A date string in YYYY-MM-DD format
 * @returns true if the date is before today (local time)
 */
export function isDateExpired(dateString: string): boolean {
  const date = parseDateAsLocal(dateString);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date < today;
}
